import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FolderKanban,
  Sparkles,
  Quote,
  Inbox,
  Settings,
  User,
  ArrowRight,
} from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const DashboardHome = () => {
  const { admin } = useAuth();
  const [stats, setStats] = useState({ projects: 0, services: 0, testimonials: 0, messages: 0, unread: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, s, t, m] = await Promise.all([
          api.listProjects(),
          api.listServices(),
          api.listTestimonials(),
          api.listMessages(),
        ]);
        setStats({
          projects: p.length,
          services: s.length,
          testimonials: t.length,
          messages: m.length,
          unread: m.filter((x) => !x.read).length,
        });
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const cards = [
    { to: "/admin/projects", label: "Projects", icon: FolderKanban, value: stats.projects, color: "var(--coral-soft)" },
    { to: "/admin/services", label: "Services", icon: Sparkles, value: stats.services, color: "var(--mint)" },
    { to: "/admin/testimonials", label: "Testimonials", icon: Quote, value: stats.testimonials, color: "var(--lilac)" },
    { to: "/admin/messages", label: "Messages", icon: Inbox, value: stats.messages, badge: stats.unread, color: "var(--butter)" },
  ];

  return (
    <div>
      <div className="text-sm uppercase tracking-wider text-[color:var(--muted)]">Dashboard</div>
      <h1 className="mt-2 font-display text-3xl md:text-5xl font-semibold leading-tight">
        Welcome back{admin?.email ? "," : "."}
        {admin?.email && <span className="text-[color:var(--coral)]"> {admin.email.split("@")[0]}</span>}
      </h1>
      <p className="mt-3 text-[color:var(--ink-soft)]">
        Edit anything on your site — text, numbers, images, services, projects, testimonials.
        Your changes go live instantly.
      </p>

      <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <Link
            key={c.to}
            to={c.to}
            className="relative p-6 rounded-[20px] bg-white border border-black/10 mk-card overflow-hidden"
          >
            <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full" style={{ backgroundColor: c.color }} />
            <div className="relative z-10">
              <c.icon className="w-5 h-5 text-[color:var(--ink)]" />
              <div className="mt-4 font-display text-3xl font-semibold">
                {loading ? "—" : c.value}
              </div>
              <div className="mt-1 text-sm text-[color:var(--ink-soft)] flex items-center gap-2">
                {c.label}
                {c.badge ? (
                  <span className="px-2 py-0.5 rounded-full bg-[color:var(--coral)] text-white text-[10px] font-semibold">
                    {c.badge} new
                  </span>
                ) : null}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="mt-12">
        <h2 className="font-display text-2xl font-semibold">Quick edits</h2>
        <div className="mt-4 grid md:grid-cols-2 gap-3">
          <QuickLink to="/admin/site" icon={Settings} title="Site info" desc="Name, email, phone, hero text, stats, marquee, footer" />
          <QuickLink to="/admin/about" icon={User} title="About section" desc="Bio, quote, designer photo, process" />
        </div>
      </div>
    </div>
  );
};

const QuickLink = ({ to, icon: Icon, title, desc }) => (
  <Link
    to={to}
    className="group flex items-start gap-4 p-5 rounded-[20px] bg-white border border-black/10 mk-card"
  >
    <span className="w-10 h-10 rounded-2xl bg-[color:var(--ink)] text-[color:var(--bg)] grid place-items-center shrink-0">
      <Icon className="w-4 h-4" />
    </span>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <span className="font-display text-lg font-semibold">{title}</span>
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
      </div>
      <div className="mt-1 text-sm text-[color:var(--ink-soft)]">{desc}</div>
    </div>
  </Link>
);

export default DashboardHome;
